import ButtonAction from 'inquiry/components/ButtonAction';
import ButtonCancel from 'inquiry/components/ButtonCancel';
import ButtonComplete from 'inquiry/components/ButtonComplete';
import ButtonCreatePersona from 'inquiry/components/ButtonCreatePersona';
import ButtonDeviceHandoff from 'inquiry/components/ButtonDeviceHandoff';
import ButtonLink from 'inquiry/components/ButtonLink';
import ButtonSubmit from 'inquiry/components/ButtonSubmit';
import ButtonVerifyWithPersona from 'inquiry/components/ButtonVerifyWithPersona';
import ClickableStack from 'inquiry/components/ClickableStack';
import Footer from 'inquiry/components/Footer';
import GovernmentIdNfcScan from 'inquiry/components/GovernmentIdNfcScan';
import HorizontalStack from 'inquiry/components/HorizontalStack';
import ImageLocal from 'inquiry/components/ImageLocal';
import ImageRemote from 'inquiry/components/ImageRemote';
import InputAddress from 'inquiry/components/InputAddress';
import InputCheckbox from 'inquiry/components/InputCheckbox';
import InputCheckboxGroup from 'inquiry/components/InputCheckboxGroup';
import InputConfirmationCode from 'inquiry/components/InputConfirmationCode';
import InputCurrency from 'inquiry/components/InputCurrency';
import InputDate from 'inquiry/components/InputDate';
import InputESignature from 'inquiry/components/InputESignature';
import InputFileUpload from 'inquiry/components/InputFileUpload';
import InputInternationalDb from 'inquiry/components/InputInternationalDb';
import InputMaskedText from 'inquiry/components/InputMaskedText';
import InputMultiSelect from 'inquiry/components/InputMultiSelect';
import InputNumber from 'inquiry/components/InputNumber';
import InputPhoneNumber from 'inquiry/components/InputPhoneNumber';
import InputRadioGroup from 'inquiry/components/InputRadioGroup';
import InputSelect from 'inquiry/components/InputSelect';
import InputText from 'inquiry/components/InputText';
import InputTextArea from 'inquiry/components/InputTextArea';
import LanguageSelector from 'inquiry/components/LanguageSelector';
import PasskeyAuthentication from 'inquiry/components/PasskeyAuthentication';
import PasskeyRegistration from 'inquiry/components/PasskeyRegistration';
import PersonaBranding from 'inquiry/components/PersonaBranding';
import PrivacyPolicy from 'inquiry/components/PrivacyPolicy';
import QrCode from 'inquiry/components/QrCode';
import Spacer from 'inquiry/components/Spacer';
import TableStatic from 'inquiry/components/TableStatic';
import Text from 'inquiry/components/Text';
import Title from 'inquiry/components/Title';
import { UiComponent, UiComponentType } from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

interface UiComponentViewProps {
  component: UiComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
  inquiryLoading: boolean;
}

export default function UiComponentView(props: UiComponentViewProps) {
  const { component, inquiry, actionsApi, inquiryLoading } = props;

  switch (component.type) {
    case UiComponentType.ButtonAction:
      return (
        <ButtonAction
          inquiry={inquiry}
          component={component}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.ButtonCancel:
      return (
        <ButtonCancel
          component={component}
          inquiry={inquiry}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.ButtonComplete:
      return (
        <ButtonComplete
          component={component}
          inquiry={inquiry}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.ButtonCreatePersona:
      return (
        <ButtonCreatePersona
          component={component}
          inquiry={inquiry}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.ButtonDeviceHandoff:
      return (
        <ButtonDeviceHandoff
          component={component}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.ButtonLink:
      return <ButtonLink inquiry={inquiry} component={component} />;
    case UiComponentType.ButtonSubmit:
      return (
        <ButtonSubmit
          inquiry={inquiry}
          component={component}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.ButtonVerifyWithPersona:
      return (
        <ButtonVerifyWithPersona
          inquiry={inquiry}
          component={component}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.Footer:
      return <Footer {...props} component={component} />;
    case UiComponentType.QrCode:
      return <QrCode component={component} inquiry={inquiry} />;
    case UiComponentType.Spacer:
      return <Spacer component={component} inquiry={inquiry} />;
    case UiComponentType.HorizontalStack:
      return <HorizontalStack {...props} component={component} />;
    case UiComponentType.ClickableStack:
      return (
        <ClickableStack
          {...props}
          inquiry={inquiry}
          component={component}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.ImageLocal:
      return <ImageLocal component={component} inquiry={inquiry} />;
    case UiComponentType.ImageRemote:
      return <ImageRemote component={component} inquiry={inquiry} />;
    case UiComponentType.LanguageSelector:
      return <LanguageSelector component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.PersonaBranding:
      return <PersonaBranding component={component} />;
    case UiComponentType.PrivacyPolicy:
      return <PrivacyPolicy component={component} inquiry={inquiry} />;
    case UiComponentType.TableStatic:
      return <TableStatic component={component} />;
    case UiComponentType.Text:
      return <Text component={component} inquiry={inquiry} />;
    case UiComponentType.Title:
      return <Title component={component} inquiry={inquiry} />;
    case UiComponentType.InputAddress:
      return (
        <InputAddress
          component={component}
          inquiry={inquiry}
          inquiryLoading={inquiryLoading}
          actionsApi={actionsApi}
        />
      );
    case UiComponentType.InputCheckbox:
      return <InputCheckbox component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputCheckboxGroup:
      return <InputCheckboxGroup component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputConfirmationCode:
      return (
        <InputConfirmationCode component={component} inquiry={inquiry} actionsApi={actionsApi} />
      );
    case UiComponentType.InputCurrency:
      return <InputCurrency component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputDate:
      return <InputDate component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputMaskedText:
      return (
        <InputMaskedText
          component={component}
          inquiry={inquiry}
          inquiryLoading={inquiryLoading}
          actionsApi={actionsApi}
        />
      );
    case UiComponentType.InputMultiSelect:
      return <InputMultiSelect component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputNumber:
      return <InputNumber component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputPhoneNumber:
      return (
        <InputPhoneNumber
          component={component}
          inquiry={inquiry}
          inquiryLoading={inquiryLoading}
          actionsApi={actionsApi}
        />
      );
    case UiComponentType.InputRadioGroup:
      return <InputRadioGroup component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputSelect:
      return <InputSelect component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputText:
      return (
        <InputText
          component={component}
          inquiry={inquiry}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.InputTextArea:
      return <InputTextArea component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.PasskeyAuthentication:
      return (
        <PasskeyAuthentication
          component={component}
          inquiry={inquiry}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.PasskeyRegistration:
      return (
        <PasskeyRegistration
          component={component}
          inquiry={inquiry}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.InputESignature:
      return <InputESignature component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputFileUpload:
      return <InputFileUpload component={component} inquiry={inquiry} actionsApi={actionsApi} />;
    case UiComponentType.InputInternationalDb:
      return (
        <InputInternationalDb
          component={component}
          inquiry={inquiry}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    case UiComponentType.GovernmentIdNfcScan:
      return (
        <GovernmentIdNfcScan
          component={component}
          inquiry={inquiry}
          actionsApi={actionsApi}
          inquiryLoading={inquiryLoading}
        />
      );
    // Components not yet implemented on web + combined step components which are special cased
    case UiComponentType.CombinedStepButton:
    case UiComponentType.CombinedStepImagePreview:
      return null;
    // See front-end/lib/interfaces/ui-components.ts:1215, Stepper serializes as a horizontalstack
    case UiComponentType.Stepper:
      return null;
    // See front-end/lib/interfaces/ui-components.ts:1223, GovernmentIdTypeSelect serializes as a horizontalstack
    case UiComponentType.GovernmentIdTypeSelect:
      return null;
    default:
      // Ignore unknown components
      captureMessage(`Unknown UI component`, 'warning', {
        component: (component as any).type,
        inquiry: inquiry.id,
      });
      assertNever(component);
  }
}
